import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Building2, MapPin, RefreshCw, Search, Shield, Users } from 'lucide-react';

import { getRequestAuthToken } from '../../auth/appliedAuthToken';
import { useAuth } from '../../contexts/AuthContext';
import CreateClientLink from '../../components/admin/client-onboarding/CreateClientLink';
import { supportsClientCreation } from '../../config/clientOnboardingExecution';

interface AdminFarmSummary {
  farm_id: string;
  name: string;
  status: string | null;
}

interface AdminClientSummary {
  org_id: string;
  name: string | null;
  status: string | null;
  user_count: number;
  user_emails: string[];
  farm_count: number | null;
  farms: AdminFarmSummary[] | null;
  canonical_data_available: boolean;
}

interface AdminClientPage {
  clients: AdminClientSummary[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    pages: number;
  };
}

type LoadState = 'loading' | 'ready' | 'unavailable';

const AdminClientsAws: React.FC = () => {
  const { t } = useTranslation(['admin_common', 'common']);
  const { user } = useAuth();
  const [queryInput, setQueryInput] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [reloadEpoch, setReloadEpoch] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [data, setData] = useState<AdminClientPage | null>(null);

  useEffect(() => {
    if (user?.role !== 'superadmin') return;

    const token = getRequestAuthToken();
    if (!token) {
      setData(null);
      setLoadState('unavailable');
      return;
    }

    let mounted = true;
    setLoadState('loading');

    const params = new URLSearchParams({ page: String(page), page_size: '20' });
    if (appliedQuery) params.set('q', appliedQuery);

    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/clients?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('admin_clients_unavailable');
        return response.json() as Promise<AdminClientPage>;
      })
      .then((payload) => {
        if (!mounted) return;
        const pagination = payload.pagination;
        if (
          !Array.isArray(payload.clients)
          || !pagination
          || !Number.isInteger(pagination.page)
          || pagination.page < 1
          || !Number.isInteger(pagination.page_size)
          || pagination.page_size < 1
          || !Number.isInteger(pagination.total)
          || pagination.total < 0
          || !Number.isInteger(pagination.pages)
          || pagination.pages < 0
        ) {
          throw new Error('admin_clients_invalid');
        }

        if (
          pagination.total > 0
          && payload.clients.length === 0
          && pagination.pages > 0
          && pagination.page > pagination.pages
        ) {
          setPage(pagination.pages);
          return;
        }

        setData(payload);
        setLoadState('ready');
      })
      .catch(() => {
        if (!mounted) return;
        setData(null);
        setLoadState('unavailable');
      });

    return () => {
      mounted = false;
    };
  }, [user?.role, appliedQuery, page, reloadEpoch]);

  if (user?.role !== 'superadmin') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="admin-clients-denied">
        <div className="text-center">
          <Shield className="mx-auto mb-4 h-14 w-14 text-red-500" />
          <h1 className="text-2xl font-bold text-gray-900">{t('admin_common:accessDenied')}</h1>
          <p className="mt-2 text-gray-600">{t('admin_common:superadminRequired')}</p>
        </div>
      </div>
    );
  }

  const statusLabel = (status: string | null): string => {
    if (status === null) return '—';
    switch (status.toLowerCase()) {
      case 'active':
        return t('admin_common:clientsDirectory.statusActive');
      case 'suspended':
        return t('admin_common:clientsDirectory.statusSuspended');
      case 'archived':
        return t('admin_common:clientsDirectory.statusArchived');
      default:
        return t('admin_common:clientsDirectory.statusUnavailable');
    }
  };

  const statusClasses = (status: string | null): string => {
    if (status === null) return 'bg-gray-100 text-gray-600';
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700';
      case 'suspended':
        return 'bg-red-50 text-red-700';
      case 'archived':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-amber-50 text-amber-700';
    }
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setAppliedQuery(queryInput.trim().slice(0, 100));
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6" data-testid="admin-clients-aws">
      <section>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin_common:sections.clients.title')}</h1>
        <p className="mt-1 text-sm text-gray-600">{t('admin_common:sections.clients.description')}</p>
        {supportsClientCreation() && <CreateClientLink key={JSON.stringify([user.id, user.org_id])} />}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <form onSubmit={submitSearch} className="flex flex-col gap-3 sm:flex-row">
          <label className="relative flex-1">
            <span className="sr-only">{t('admin_common:clientsDirectory.searchLabel')}</span>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={queryInput}
              onChange={(event) => setQueryInput(event.target.value)}
              maxLength={100}
              placeholder={t('admin_common:clientsDirectory.searchPlaceholder')}
              className="min-h-[44px] w-full rounded-xl border border-gray-200 pl-10 pr-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              data-testid="admin-clients-search"
            />
          </label>
          <button
            type="submit"
            className="min-h-[44px] rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            {t('common:search')}
          </button>
        </form>
      </section>

      {loadState === 'loading' && (
        <section
          className="flex min-h-[220px] items-center justify-center rounded-2xl border border-gray-200 bg-white"
          data-testid="admin-clients-loading"
          aria-label={t('common:loading')}
        >
          <RefreshCw className="h-7 w-7 animate-spin text-gray-400" />
        </section>
      )}

      {loadState === 'unavailable' && (
        <section
          className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800"
          role="status"
          data-testid="admin-clients-unavailable"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <h2 className="font-semibold">{t('admin_common:clientsDirectory.unavailableTitle')}</h2>
              <p className="mt-1 text-sm">{t('admin_common:clientsDirectory.unavailableMessage')}</p>
              <button
                type="button"
                onClick={() => setReloadEpoch((value) => value + 1)}
                className="mt-3 inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-semibold text-red-700"
                data-testid="admin-clients-retry"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common:refresh')}
              </button>
            </div>
          </div>
        </section>
      )}

      {loadState === 'ready' && data && (
        <>
          <section className="flex items-end justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {t('admin_common:clientsDirectory.detected')}: {data.pagination.total}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">{t('admin_common:clientsDirectory.platformExcluded')}</p>
            </div>
            <button
              type="button"
              onClick={() => setReloadEpoch((value) => value + 1)}
              className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" />
              {t('common:refresh')}
            </button>
          </section>

          {data.clients.length === 0 ? (
            <section className="rounded-2xl border border-gray-200 bg-white p-10 text-center" data-testid="admin-clients-empty">
              <Users className="mx-auto h-12 w-12 text-gray-300" />
              <h2 className="mt-3 font-semibold text-gray-800">{t('admin_common:clientsDirectory.emptyTitle')}</h2>
              <p className="mx-auto mt-1 max-w-lg text-sm text-gray-500">{t('admin_common:clientsDirectory.emptyMessage')}</p>
            </section>
          ) : (
            <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {data.clients.map((client) => (
                <article
                  key={client.org_id}
                  className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6"
                  data-testid={`admin-client-${client.org_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 shrink-0 text-emerald-700" />
                        <h2 className="truncate text-lg font-semibold text-gray-900">{client.name || client.org_id}</h2>
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-500">{client.org_id}</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses(client.status)}`}
                      data-testid={`admin-client-status-${client.org_id}`}
                    >
                      {statusLabel(client.status)}
                    </span>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-xl bg-gray-50 p-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4" />
                        <span className="text-xs font-semibold uppercase tracking-wide">{t('admin_common:clientsDirectory.users')}</span>
                      </div>
                      <p className="mt-2 text-xl font-semibold text-gray-900">{client.user_count}</p>
                      <div className="mt-2 space-y-1">
                        {client.user_emails.length === 0 ? (
                          <p className="text-xs text-gray-500">—</p>
                        ) : (
                          client.user_emails.slice(0, 3).map((email) => (
                            <p key={email} className="truncate text-xs text-gray-600">{email}</p>
                          ))
                        )}
                        {client.user_emails.length > 3 && (
                          <p className="text-xs font-medium text-gray-500">+{client.user_emails.length - 3}</p>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl bg-gray-50 p-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span className="text-xs font-semibold uppercase tracking-wide">{t('admin_common:clientsDirectory.farms')}</span>
                      </div>
                      <p className="mt-2 text-xl font-semibold text-gray-900">{client.farm_count === null ? '—' : client.farm_count}</p>
                      {client.farms !== null && client.farms.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {client.farms.slice(0, 4).map((farm) => (
                            <span key={farm.farm_id} className="rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                              {farm.name}
                            </span>
                          ))}
                          {client.farms.length > 4 && (
                            <span className="rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-500">
                              +{client.farms.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {!client.canonical_data_available && (
                    <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{t('admin_common:clientsDirectory.canonicalUnavailable')}</span>
                    </div>
                  )}
                </article>
              ))}
            </section>
          )}

          {data.pagination.pages > 1 && (
            <nav
              className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3"
              aria-label={t('admin_common:clientsDirectory.paginationLabel')}
            >
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={data.pagination.page <= 1}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-40"
              >
                {t('common:previous')}
              </button>
              <span className="text-sm text-gray-500">{t('common:page')} {data.pagination.page}</span>
              <button
                type="button"
                onClick={() => setPage((value) => Math.min(data.pagination.pages, value + 1))}
                disabled={data.pagination.page >= data.pagination.pages}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:opacity-40"
              >
                {t('common:next')}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default AdminClientsAws;
